/**
 * @fileoverview Root application component for the Hans web app.
 *
 * Sets up React Router, authentication context, and protected routes.
 * Provides the AuthContext for sharing user state across the component tree.
 */

import React, { useEffect, useState, useMemo, useCallback, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import TimezoneHint from "@/components/TimezoneHint";
import ThemeRouteSync from "@/components/ThemeRouteSync";

import * as Sentry from "@sentry/react";
import * as analytics from "@/lib/analytics";
import { BILLING_ENABLED, MARKETPLACE_ENABLED, SCHEDULING_ENABLED, SCHEDULER_VIEW_ENABLED } from "@/lib/featureFlags";
import LandingPage from "@/pages/LandingPage";
import InstructorLoginPage from "@/pages/InstructorLoginPage";
import AuthPortalLayout from "@/components/auth/AuthPortalLayout";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import Dashboard from "@/pages/Dashboard";
import StudentRoster from "@/pages/StudentRoster";
import StudentProfile from "@/pages/StudentProfile";
import PracticeRecordingPage from "@/pages/PracticeRecordingPage";
import ClassCreate from "@/pages/ClassCreate";
import ClassDetail from "@/pages/ClassDetail";
import HansRecording from "@/pages/HansRecording";
import LessonSummary from "@/pages/LessonSummary";
import ManualLessonCompose from "@/pages/ManualLessonCompose";
import SchedulerLessonPage from "@/pages/SchedulerLessonPage";
import AllLessonsPage from "@/pages/AllLessonsPage";
import SavedRecordingsPage from "@/pages/SavedRecordingsPage";
import ConsentLedgerPage from "@/pages/ConsentLedgerPage";
import MetronomePage from "@/pages/MetronomePage";
import TunerPage from "@/pages/TunerPage";
import ToolsPage from "@/pages/ToolsPage";
import StudioLayout from "@/components/studio/StudioLayout";
import StudentPortalLayout from "@/components/studio/StudentPortalLayout";
import SchedulePage from "@/pages/SchedulePage";
import CalendarPage from "@/pages/CalendarPage";
import ClassSessionsPage from "@/pages/ClassSessionsPage";
import NotesOwedPage from "@/pages/NotesOwedPage";
import BillingPage from "@/pages/BillingPage";
import BookingPage from "@/pages/BookingPage";
import SettingsPage from "@/pages/SettingsPage";
import RepertoirePage from "@/pages/RepertoirePage";
import SavedRepertoiresPage from "@/pages/SavedRepertoiresPage";
import OnboardingPage from "@/pages/OnboardingPage";
import FoundingInvitePage from "@/pages/founding/FoundingInvitePage";
import StudentInvitePage from "@/pages/student/StudentInvitePage";
import ConsentPage from "@/pages/ConsentPage";
import SubstituteSessionPage from "@/pages/SubstituteSessionPage";
import ConsentRevokePage from "@/pages/ConsentRevokePage";
import StudentLoginPage from "@/pages/student/StudentLoginPage";
import StudentDashboard from "@/pages/student/StudentDashboard";
import StudentLessonsPage from "@/pages/student/StudentLessonsPage";
import StudentLessonView from "@/pages/student/StudentLessonView";
import StudentPracticePage from "@/pages/student/StudentPracticePage";
import StudentProfilePage from "@/pages/student/StudentProfilePage";
import StudentPortfolioPage from "@/pages/student/StudentPortfolioPage";
import StudentProgressPage from "@/pages/student/StudentProgressPage";
import PublicPortfolioPage from "@/pages/student/PublicPortfolioPage";
import ParentLoginPage from "@/pages/parent/ParentLoginPage";
import ParentDashboard, { ParentChildPage } from "@/pages/parent/ParentDashboard";
import ChildModePage from "@/pages/parent/ChildModePage";
import { ParentAuthProvider, useParentAuth } from "@/contexts/ParentAuthContext";
import PricingPage from "@/pages/PricingPage";
import FeaturePage from "@/pages/features/FeaturePage";
import AcademyOnboardingPage from "@/pages/AcademyOnboardingPage";
import AcademyWalkthroughPage from "@/pages/AcademyWalkthroughPage";
import FeedbackPage from "@/pages/FeedbackPage";
import ContactPage from "@/pages/ContactPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import SecurityPage from "@/pages/SecurityPage";
import SupportPage from "@/pages/SupportPage";
import RecordingConsentPage from "@/pages/RecordingConsentPage";
import ClipsPage from "@/pages/ClipsPage";
import GuidePage from "@/pages/GuidePage";
import StudentGuidePage from "@/pages/StudentGuidePage";
import OrgGuidePage from "@/pages/OrgGuidePage";
import ViewerGuidePage from "@/pages/ViewerGuidePage";
import OrganizationsPage from "@/pages/OrganizationsPage";
import CheckoutReturnPage from "@/pages/CheckoutReturnPage";
import OrgLoginPage from "@/pages/org/OrgLoginPage";
import OrgSignupPage from "@/pages/org/OrgSignupPage";
import OrgVerifyEmailPage from "@/pages/org/OrgVerifyEmailPage";
import OrgLayout from "@/components/org/OrgLayout";
import OrgSettingsLayout, { MovedTo } from "@/components/org/OrgSettingsLayout";
import OrgSettingsPage from "@/pages/org/OrgSettingsPage";
import { ORG_SETTINGS_SECTIONS } from "@/lib/orgSettings";
import { orgLoginPath, portalRedirect } from "@/lib/orgNext";
import OrgInstructorsPage from "@/pages/org/OrgInstructorsPage";
import OrgInstructorDetailPage from "@/pages/org/OrgInstructorDetailPage";
import OrgBrandingPage from "@/pages/org/OrgBrandingPage";
import OrgFeaturesPage from "@/pages/org/OrgFeaturesPage";
import OrgDashboardPage from "@/pages/org/OrgDashboardPage";
import OrgActivityPage from "@/pages/org/OrgActivityPage";
import OrgNotificationsPage from "@/pages/org/OrgNotificationsPage";
import OrgViewerHomePage from "@/pages/org/OrgViewerHomePage";
import OrgViewerProgramPage from "@/pages/org/OrgViewerProgramPage";
import OrgViewerProfilePage from "@/pages/org/OrgViewerProfilePage";
import OrgAdminsPage from "@/pages/org/OrgAdminsPage";
import OrgAccountPage from "@/pages/org/OrgAccountPage";
import OrgAppearancePage from "@/pages/org/OrgAppearancePage";
import OrgSubscriptionPage from "@/pages/org/OrgSubscriptionPage";
import OrgProgramsPage from "@/pages/org/OrgProgramsPage";
import OrgProgramDetailPage from "@/pages/org/OrgProgramDetailPage";
import OrgClassDetailPage from "@/pages/org/OrgClassDetailPage";
import OrgCalendarPage from "@/pages/org/OrgCalendarPage";
import OrgUsagePage from "@/pages/org/OrgUsagePage";
import OrgSchedulePage from "@/pages/org/OrgSchedulePage";
import OpsLoginPage from "@/pages/ops/OpsLoginPage";
import OpsLayout from "@/components/ops/OpsLayout";
import OpsFlagsPage from "@/pages/ops/OpsFlagsPage";
import OpsActivityPage from "@/pages/ops/OpsActivityPage";
import OpsFoundingPage from "@/pages/ops/OpsFoundingPage";
import OpsAccountPage from "@/pages/ops/OpsAccountPage";
import ProgramsPage from "@/pages/ProgramsPage";
import { programsPath } from "@/lib/programsRoute";
import { stashReturnTo } from "@/lib/returnTo";
import ProgramsDirectoryPage from "@/pages/ProgramsDirectoryPage";
import ProgramPage from "@/pages/ProgramPage";

/** @type {string} Base URL for all API requests */
import BACKEND_URL from "@/lib/backendUrl";
if (!BACKEND_URL) {
  console.error("REACT_APP_BACKEND_URL is not configured");
}
export const API = `${BACKEND_URL}/api`;

/**
 * React context for sharing authenticated user state.
 * Provides { user, setUser } to all protected child components.
 * @type {React.Context<{user: object, setUser: function}|null>}
 */
export const AuthContext = React.createContext(null);

/**
 * Auth provider that checks authentication once and shares state across all routes.
 * Calls /auth/me on mount. Accepts user data from navigation state (post-login).
 * Redirects to landing page if unauthenticated.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Routes to render when authenticated.
 * @returns {React.ReactElement}
 */
const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user was passed from auth callback, use it directly
    if (location.state?.user && !user) {
      const u = location.state.user;
      setUser(u);
      setIsAuthenticated(true);
      analytics.identify(u);
      if (!u.onboarding_completed && location.pathname !== "/onboarding") {
        navigate("/onboarding", { replace: true });
      }
      return;
    }

    // Skip auth check if already authenticated
    if (isAuthenticated) return;

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
        });
        setUser(response.data);
        setIsAuthenticated(true);
        analytics.identify(response.data);
        if (!response.data.onboarding_completed && location.pathname !== "/onboarding") {
          navigate("/onboarding", { replace: true });
        }
      } catch (_error) {
        setIsAuthenticated(false);
        analytics.reset();
        // An emailed link into the app (a missed-notes alert's "Add notes")
        // must survive the sign-in it runs into.
        stashReturnTo(`${location.pathname}${location.search}`);
        navigate("/");
      }
    };

    checkAuth();
  }, [navigate, location.state, user, isAuthenticated, location.pathname, location.search]);

  /**
   * Re-read /auth/me and refresh the cached user.
   *
   * The shell renders several things off this payload that a page can change
   * — the Programs invitation dot is the first — and without a way to re-read
   * it, answering the last invitation leaves the dot up until a hard reload.
   * Deliberately does NOT touch isAuthenticated: a transient failure here is
   * a stale badge, not a reason to sign someone out mid-task.
   */
  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      return response.data;
    } catch (_error) {
      return null;
    }
  }, []);

  const contextValue = useMemo(() => ({ user, setUser, refreshUser }), [user, refreshUser]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center studio-spotlight">
        <div className="flex items-center justify-center gap-3 mb-10">
          <img src="/logo192.png" alt="Hans" className="h-10 rounded-lg" />
          <span className="font-serif text-lg font-semibold tracking-tight text-[var(--text-primary)]">Hans</span>
        </div>
        <div className="flex items-end justify-center gap-[3px] h-16">
          {[28,42,20,50,35,55,25,45,30,52,22,48,38,44,26,40].map((h, i) => (
            <div key={i} className="soundwave-bar" style={{ height: `${h}px`, animationDelay: `${i * 0.07}s` }} />
          ))}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {/* The browser's zone, offered once per load — stored when the account
          has none, asked about when it disagrees (decision D3). */}
      <TimezoneHint user={user} setUser={setUser} />
      {children}
    </AuthContext.Provider>
  );
};

/**
 * React context for sharing authenticated student state.
 * Provides { student, setStudent } to all student portal components.
 */
export const StudentAuthContext = React.createContext(null);

/**
 * Auth provider for the student portal. Checks /student/auth/me on mount.
 * Redirects to /student/login if unauthenticated.
 */
const StudentAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [student, setStudent] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // A student session is never identified to analytics: its events stay
  // anonymous, as on iOS (lib/analytics identifies instructors only).
  useEffect(() => {
    if (location.state?.student && !student) {
      setStudent(location.state.student);
      setIsAuthenticated(true);
      return;
    }

    if (isAuthenticated) return;

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/student/auth/me`, {
          withCredentials: true,
        });
        setStudent(response.data);
        setIsAuthenticated(true);
      } catch (_error) {
        setIsAuthenticated(false);
        analytics.reset();
        navigate("/student/login");
      }
    };

    checkAuth();
  }, [navigate, location.state, student, isAuthenticated]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/student/auth/logout`, {}, { withCredentials: true });
    } catch (_) { /* ignore */ }
    setStudent(null);
    setIsAuthenticated(false);
    navigate("/student/login");
  }, [navigate]);

  const contextValue = useMemo(() => ({ student, setStudent, logout }), [student, logout]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center studio-spotlight">
        <div className="flex items-center justify-center gap-3 mb-10">
          <img src="/logo192.png" alt="Hans" className="h-10 rounded-lg" />
          <span className="font-serif text-lg font-semibold tracking-tight text-[var(--text-primary)]">Hans</span>
        </div>
        <div className="flex items-end justify-center gap-[3px] h-16">
          {[28,42,20,50,35,55,25,45,30,52,22,48,38,44,26,40].map((h, i) => (
            <div key={i} className="soundwave-bar" style={{ height: `${h}px`, animationDelay: `${i * 0.07}s` }} />
          ))}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/student/login" />;
  }

  return (
    <StudentAuthContext.Provider value={contextValue}>
      {children}
    </StudentAuthContext.Provider>
  );
};

/**
 * React context for sharing authenticated org-admin state (Spec 11).
 * Provides { admin, org, setOrg, branding, features, flags, refresh, logout };
 * `flags` are the runtime switches the org portal resolves itself (Spec 19).
 */
export const OrgAuthContext = React.createContext(null);

/**
 * Auth provider for the organization-admin portal. Checks /org/auth/me on
 * mount (which returns { admin, org, branding, features }). Redirects to
 * /org/login if unauthenticated.
 */
const OrgAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [org, setOrg] = useState(null);
  const [branding, setBranding] = useState(null);
  const [features, setFeatures] = useState(null);
  const [flags, setFlags] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const applyMe = useCallback((data) => {
    setAdmin(data.admin);
    setOrg(data.org);
    setBranding(data.branding);
    setFeatures(data.features);
    setFlags(data.flags || null);
  }, []);

  useEffect(() => {
    if (isAuthenticated) return;

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/org/auth/me`, { withCredentials: true });
        applyMe(response.data);
        setIsAuthenticated(true);
      } catch (_error) {
        setIsAuthenticated(false);
        // Back to this page after signing in: an email's "Open the program"
        // opened signed out must still end on the program (lib/orgNext).
        navigate(orgLoginPath(location));
      }
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-checked per page, as before; the search rides along
  }, [navigate, isAuthenticated, location.pathname, applyMe]);


  // Re-pull the org after a branding/feature edit so the shell updates live.
  const refresh = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/org/auth/me`, { withCredentials: true });
      applyMe(response.data);
    } catch (_) { /* ignore */ }
  }, [applyMe]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/org/auth/logout`, {}, { withCredentials: true });
    } catch (_) { /* ignore */ }
    setAdmin(null);
    setOrg(null);
    setIsAuthenticated(false);
    navigate("/org/login");
  }, [navigate]);

  const contextValue = useMemo(
    () => ({ admin, org, setOrg, branding, features, flags, refresh, logout }),
    [admin, org, branding, features, flags, refresh, logout],
  );

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center studio-spotlight">
        <div className="flex items-center justify-center gap-3 mb-10">
          <img src="/logo192.png" alt="Hans" className="h-10 rounded-lg" />
          <span className="font-serif text-lg font-semibold tracking-tight text-[var(--text-primary)]">Hans</span>
        </div>
        <div className="flex items-end justify-center gap-[3px] h-16">
          {[28,42,20,50,35,55,25,45,30,52,22,48,38,44,26,40].map((h, i) => (
            <div key={i} className="soundwave-bar" style={{ height: `${h}px`, animationDelay: `${i * 0.07}s` }} />
          ))}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={orgLoginPath(location)} />;
  }

  /* A program viewer has their own pages and nothing else here: every admin
     route refuses them, so an admin page would render as a wall of errors.
     Redirected before any admin page mounts (and fires its reads), wherever
     they land — sign-in goes to /org. An address that names a program opens
     the viewer's own page for it, and an administrator handed a viewer's
     program address opens theirs (lib/orgNext). */
  const elsewhere = portalRedirect(admin?.role, location.pathname);
  if (elsewhere) {
    return <Navigate to={elsewhere} replace />;
  }

  return (
    <OrgAuthContext.Provider value={contextValue}>
      {children}
    </OrgAuthContext.Provider>
  );
};

/**
 * Gate for the parent portal (F1) — the FIFTH session type, on its own
 * `parent_session_token` cookie.
 *
 * Deliberately thinner than the other portal gates: a parent has no sidebar
 * and no nav, because the whole surface is "your children" and one child's
 * lessons. A shell would be chrome around two pages.
 */
const ParentGate = () => {
  return (
    <ParentAuthProvider>
      <ParentGateInner />
    </ParentAuthProvider>
  );
};

const ParentGateInner = () => {
  const { parent, loading } = useParentAuth();
  if (loading) return null;
  if (!parent) return <Navigate to="/parent/login" replace />;
  return <Outlet />;
};

/**
 * React context for sharing the signed-in ops operator.
 * Provides { admin, refresh, logout }.
 */
export const OpsAuthContext = React.createContext(null);

/**
 * Auth provider for the internal ops console — the FOURTH session type, on its
 * own `platform_session_token` cookie. Checks /ops/auth/me on mount (which
 * returns { admin }) and redirects to /ops/login if unauthenticated.
 */
const OpsAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [admin, setAdmin] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) return;

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/ops/auth/me`, { withCredentials: true });
        setAdmin(response.data.admin);
        setIsAuthenticated(true);
      } catch (_error) {
        setIsAuthenticated(false);
        navigate("/ops/login");
      }
    };

    checkAuth();
  }, [navigate, isAuthenticated]);

  const refresh = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ops/auth/me`, { withCredentials: true });
      setAdmin(response.data.admin);
    } catch (_) { /* ignore */ }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/ops/auth/logout`, {}, { withCredentials: true });
    } catch (_) { /* ignore */ }
    setAdmin(null);
    setIsAuthenticated(false);
    navigate("/ops/login");
  }, [navigate]);

  const contextValue = useMemo(() => ({ admin, refresh, logout }), [admin, refresh, logout]);

  if (isAuthenticated === null) {
    return (
      <div className="studio min-h-screen flex flex-col items-center justify-center bg-[var(--studio-base)]">
        <div className="flex items-center justify-center gap-3 mb-10">
          <img src="/logo192.png" alt="Hans" className="h-10 rounded-lg" />
          <span className="font-serif text-lg font-semibold tracking-tight text-[var(--text-primary)]">Hans Ops</span>
        </div>
        <div className="flex items-end justify-center gap-[3px] h-16">
          {[28,42,20,50,35,55,25,45,30,52,22,48,38,44,26,40].map((h, i) => (
            <div key={i} className="soundwave-bar" style={{ height: `${h}px`, animationDelay: `${i * 0.07}s` }} />
          ))}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/ops/login" />;
  }

  return (
    <OpsAuthContext.Provider value={contextValue}>
      {children}
    </OpsAuthContext.Provider>
  );
};

/**
 * Application router defining all page routes.
 * Protected routes are wrapped in a single AuthProvider so auth state
 * persists across route changes without redundant /auth/me calls.
 * @returns {React.ReactElement} The Routes configuration.
 */
/**
 * /marketplace was renamed to /instructors/:userId/programs. Keep the old path
 * working — it has been in the sidebar for months, so it is in browser
 * histories and bookmarks. Redirects to the caller's own canonical Programs URL.
 */
function LegacyMarketplaceRedirect() {
  const { user } = React.useContext(AuthContext) || {};
  return <Navigate to={programsPath(user)} replace />;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/features/:featureSlug" element={<FeaturePage />} />
      {/* The organization overview. The three /features/organization-* pages
          are deep-dives off it; this is the single link to send a school. */}
      <Route path="/for-organizations" element={<OrganizationsPage />} />
      <Route path="/feedback" element={<FeedbackPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/support" element={<SupportPage />} />
      {/* Unlisted recording-consent reference page — deliberately linked from
          nowhere in the app; shared as a direct link on external sites. */}
      <Route path="/recording-consent" element={<RecordingConsentPage />} />

      {/* Public practice clips, reached from the link in a summary email. Token
          is the authorization — this has to work for a parent with no account,
          which is the whole point of the email delivery path. */}
      <Route path="/clips/:token" element={<ClipsPage />} />
      {/* The Academy's video walkthroughs: the hub, and each film's own page
          (the address to share). Not /onboarding, the post-signup wizard. */}
      <Route path="/academy/onboarding" element={<AcademyOnboardingPage />} />
      <Route path="/academy/onboarding/:slug" element={<AcademyWalkthroughPage />} />
      <Route path="/guide" element={<GuidePage />} />
      <Route path="/student-guide" element={<StudentGuidePage />} />
      {/* One Academy, three audiences. Org admins used to be sent to the
          instructor guide, which documents a portal they cannot sign in to. */}
      <Route path="/org-guide" element={<OrgGuidePage />} />
      <Route path="/viewer-guide" element={<ViewerGuidePage />} />
      {/* The three sign-in portals share ONE shell (brand, tab strip, heading)
          so switching audience is a highlight sliding rather than a page
          reload. They keep their own URLs on purpose: the three 401
          interceptors and every emailed link already point at them, and each
          path is what selects the tab. */}
      <Route element={<AuthPortalLayout />}>
        <Route path="/login" element={<InstructorLoginPage />} />
        <Route path="/student/login" element={<StudentLoginPage />} />
        <Route path="/org/login" element={<OrgLoginPage />} />
      </Route>
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {/* Public password-reset routes (no auth — request + tokenized link) */}
      <Route path="/forgot-password" element={<ForgotPasswordPage portal="instructor" />} />
      <Route path="/reset-password" element={<ResetPasswordPage portal="instructor" />} />
      {/* Public consent routes (no auth — opened from emails). The revoke
          route is declared first so "revoke" isn't captured as a :token. */}
      <Route path="/consent/revoke/:token" element={<ConsentRevokePage />} />
      <Route path="/consent/:token" element={<ConsentPage />} />
      {/* A substitute's session (docs/substitute-requests.md): the emailed link
          is the credential, so it sits outside every portal's auth. */}
      <Route path="/sub/:token" element={<SubstituteSessionPage />} />
      {/* Public booking page (no auth — token in the URL is the authorization) */}
      {SCHEDULING_ENABLED && <Route path="/book/:token" element={<BookingPage />} />}
      {/* Public program directory (marketplace — no auth). The book route is
          declared before the :programId detail so "book" isn't captured. */}
      {MARKETPLACE_ENABLED && <Route path="/programs/:programId/book/:userId" element={<BookingPage />} />}
      {MARKETPLACE_ENABLED && <Route path="/programs/:programId" element={<ProgramPage />} />}
      {MARKETPLACE_ENABLED && <Route path="/programs" element={<ProgramsDirectoryPage />} />}
      {/* Public student routes (no auth) */}
      {/* Founding Ensemble cohort (Phase One). UNLISTED BY DESIGN: reachable
          only with a token the founder emails personally, and linked from
          nowhere in the app — no nav item, no pricing-page line, no sitemap
          entry. Adding a link anywhere breaks the one property the cohort
          rests on. */}
      <Route path="/founding/:token" element={<FoundingInvitePage />} />
      <Route path="/student/invite/:token" element={<StudentInvitePage />} />
      {/* /student/login is declared with the other two portals above. */}
      <Route path="/student/forgot-password" element={<ForgotPasswordPage portal="student" />} />
      <Route path="/student/reset-password" element={<ResetPasswordPage portal="student" />} />
      <Route path="/portfolio/:accountId" element={<PublicPortfolioPage />} />
      {/* A student's chosen link (F8). Additional to the account-id route
          above, never a replacement — a shared link must not stop working. */}
      <Route path="/p/:handle" element={<PublicPortfolioPage />} />
      {/* Public org-admin auth routes (no auth — the portal itself is gated
          below). /org/login is declared with the other two portals above. */}
      <Route path="/org/forgot-password" element={<ForgotPasswordPage portal="organization" />} />
      <Route path="/org/signup" element={<OrgSignupPage />} />
      <Route path="/org/verify-email" element={<OrgVerifyEmailPage />} />
      <Route path="/org/reset-password" element={<ResetPasswordPage portal="organization" />} />
      {/* Protected org-admin portal (Spec 11). OrgAuthProvider gates the tree;
          OrgLayout provides the persistent left sidebar shell. */}
      <Route path="/org" element={<OrgAuthProvider><OrgLayout /></OrgAuthProvider>}>
        <Route index element={<OrgDashboardPage />} />
        <Route path="instructors" element={<OrgInstructorsPage />} />
        <Route path="instructors/:userId" element={<OrgInstructorDetailPage />} />
        <Route path="programs" element={<OrgProgramsPage />} />
        <Route path="programs/:programId" element={<OrgProgramDetailPage />} />
        <Route path="classes/:classId" element={<OrgClassDetailPage />} />
        <Route path="calendar" element={<OrgCalendarPage />} />
        <Route path="usage" element={<OrgUsagePage />} />
        {/* Reporting became the Dashboard; its tabs and range ride along. */}
        <Route path="reporting" element={<MovedTo to="/org" />} />
        {/* Settings: a hub, then one page per section (lib/orgSettings.js),
            each under a breadcrumb back to the hub. */}
        <Route path="settings" element={<OrgSettingsLayout />}>
          <Route index element={<OrgSettingsPage />} />
          <Route path="account" element={<OrgAccountPage />} />
          <Route path="appearance" element={<OrgAppearancePage />} />
          <Route path="plan" element={<OrgSubscriptionPage />} />
          <Route path="admins" element={<OrgAdminsPage />} />
          <Route path="branding" element={<OrgBrandingPage />} />
          <Route path="features" element={<OrgFeaturesPage />} />
          <Route path="notifications" element={<OrgNotificationsPage />} />
          <Route path="schedule" element={<OrgSchedulePage />} />
          <Route path="activity" element={<OrgActivityPage />} />
          <Route path="*" element={<Navigate to="/org/settings" replace />} />
        </Route>
        {/* Where the settings sections lived before they moved under Settings.
            MovedTo keeps the query string: Stripe's checkout still returns to
            /org/subscription?checkout=…, and the Plan page reads it. */}
        {ORG_SETTINGS_SECTIONS.filter((s) => s.legacyPath).map((s) => (
          <Route key={s.id} path={s.legacyPath} element={<MovedTo to={s.path} />} />
        ))}
        {/* O5 retired seats; keep the old path pointed somewhere real so a
            bookmarked link lands on the plan rather than the dashboard. */}
        <Route path="seats" element={<Navigate to="/org/settings/plan" replace />} />
        {/* Invoice history is retired from this portal; /org/billing falls
            through to the dashboard below. Notifications moved under
            Settings (iOS review C10); its old path redirects with the rest. */}
        {/* A program viewer's own pages (see OrgAuthProvider). */}
        <Route path="viewer" element={<OrgViewerHomePage />} />
        <Route path="viewer/programs/:programId" element={<OrgViewerProgramPage />} />
        <Route path="viewer/profile" element={<OrgViewerProfilePage />} />
        <Route path="*" element={<Navigate to="/org" replace />} />
      </Route>
      {/* Parent portal (F1) — the fifth identity. Public auth route first;
          the tree below is gated by ParentAuthProvider. */}
      <Route path="/parent/login" element={<ParentLoginPage />} />
      <Route path="/parent/forgot-password" element={<ForgotPasswordPage portal="parent" />} />
      <Route path="/parent/reset-password" element={<ResetPasswordPage portal="parent" />} />
      {/* Child mode (F10) sits OUTSIDE ParentGate deliberately: the session it
          runs in is a NARROWING of the parent's, so /parent/auth/me 403s for
          it by design and the gate would bounce a child straight to a login
          page they cannot use. */}
      <Route path="/parent/child" element={<ChildModePage />} />
      <Route path="/parent" element={<ParentGate />}>
        <Route index element={<ParentDashboard />} />
        <Route path="children/:studentId" element={<ParentChildPage />} />
        <Route path="*" element={<Navigate to="/parent" replace />} />
      </Route>

      {/* Public ops-console auth routes (no auth — the console itself is gated below) */}
      <Route path="/ops/login" element={<OpsLoginPage />} />
      <Route path="/ops/forgot-password" element={<ForgotPasswordPage portal="platform" />} />
      <Route path="/ops/reset-password" element={<ResetPasswordPage portal="platform" />} />
      {/* Protected internal ops console. OpsAuthProvider gates the tree;
          OpsLayout provides the persistent left sidebar shell. */}
      <Route path="/ops" element={<OpsAuthProvider><OpsLayout /></OpsAuthProvider>}>
        <Route index element={<OpsFlagsPage />} />
        <Route path="founding" element={<OpsFoundingPage />} />
        <Route path="activity" element={<OpsActivityPage />} />
        <Route path="account" element={<OpsAccountPage />} />
        <Route path="*" element={<Navigate to="/ops" replace />} />
      </Route>
      {/* Protected student portal routes. StudentAuthProvider gates the tree;
          the StudentPortalLayout child route provides the persistent left
          sidebar shell (fixed rail on md+, drawer on mobile). */}
      <Route path="/student" element={<StudentAuthProvider><StudentPortalLayout /></StudentAuthProvider>}>
        <Route path="dashboard" element={<StudentDashboard />} />
        <Route path="lessons" element={<StudentLessonsPage />} />
        <Route path="lessons/:lessonId" element={<StudentLessonView />} />
        <Route path="rehearsals" element={<StudentPracticePage />} />
        <Route path="practice" element={<Navigate to="/student/rehearsals" replace />} />
        {/* Portfolio became Progress, with sharing behind it (C7, Nov 2026). */}
        <Route path="progress" element={<StudentProgressPage />} />
        <Route path="progress/share" element={<StudentPortfolioPage />} />
        <Route path="portfolio" element={<Navigate to="/student/progress/share" replace />} />
        <Route path="profile" element={<StudentProfilePage />} />
        <Route path="*" element={<Navigate to="/student/dashboard" replace />} />
      </Route>
      {/* Protected instructor routes. AuthProvider gates the whole tree; the
          StudioLayout child route provides the persistent left sidebar. A few
          routes render full-screen (no sidebar): the record cockpit, onboarding,
          and the Stripe checkout return. */}
      <Route element={<AuthProvider><Outlet /></AuthProvider>}>
        {/* Full-screen (no sidebar shell) */}
        <Route path="/onboarding" element={<OnboardingPage />} />
        {/* The welcome is a dialog over the dashboard now, not a page in
            front of it. The old URL still resolves — it was the stable way
            back to the welcome, and links to it exist. */}
        <Route path="/founding/welcome" element={<Navigate to="/dashboard?welcome=1" replace />} />
        <Route path="/hans" element={<HansRecording />} />
        <Route path="/hans/class/:classId" element={<HansRecording />} />
        <Route path="/hans/:studentId" element={<HansRecording />} />
        <Route path="/checkout/return" element={<CheckoutReturnPage />} />
        {/* Sidebar shell */}
        <Route element={<StudioLayout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/scheduler/lesson" element={<SchedulerLessonPage />} />
          {SCHEDULING_ENABLED && !SCHEDULER_VIEW_ENABLED && <Route path="/schedule" element={<SchedulePage />} />}
          {/* Post-cutover counterpart of /schedule, and the destination of the
              sidebar's Calendar item. Registered under the same flag pair that
              chooses between them, so the nav can never point at a route that
              isn't mounted. */}
          {SCHEDULING_ENABLED && SCHEDULER_VIEW_ENABLED && <Route path="/calendar" element={<CalendarPage />} />}
          {/* Both read the scheduling mirror, so both are mounted under the
              same pair as /calendar — their endpoints are 404 before the
              cutover, and a route that can only error is worse than no route.
              Every link into them is gated on the same flags for that reason. */}
          {SCHEDULING_ENABLED && SCHEDULER_VIEW_ENABLED && <Route path="/notes-owed" element={<NotesOwedPage />} />}
          {/* A class's sessions live under its program (I8), so the Programs
              item stays lit and Back returns to the program. The old path is
              kept: the page redirects a program class to its new address and
              still serves a personal class there. */}
          {SCHEDULING_ENABLED && SCHEDULER_VIEW_ENABLED && (
            <Route path="/classes/:classId/sessions" element={<ClassSessionsPage />} />
          )}
          {SCHEDULING_ENABLED && SCHEDULER_VIEW_ENABLED && MARKETPLACE_ENABLED && (
            <Route path="/instructors/:userId/programs/:programId/classes/:classId" element={<ClassSessionsPage />} />
          )}
          {BILLING_ENABLED && <Route path="/billing" element={<BillingPage />} />}
          {/* Programs (was /marketplace). The path carries the instructor's own
              user id because /programs is already the PUBLIC student directory
              — see lib/programsRoute. The legacy path redirects so existing
              bookmarks survive the rename. */}
          {MARKETPLACE_ENABLED && <Route path="/instructors/:userId/programs" element={<ProgramsPage />} />}
          {MARKETPLACE_ENABLED && <Route path="/instructors/:userId/programs/:programId" element={<ProgramsPage />} />}
          {MARKETPLACE_ENABLED && <Route path="/marketplace" element={<LegacyMarketplaceRedirect />} />}
          <Route path="/students" element={<StudentRoster />} />
          <Route path="/students/:studentId" element={<StudentProfile />} />
          {/* A rehearsal is reviewed from the student's page (B5), not the lesson's. */}
          <Route path="/students/:studentId/practice/:recordingId" element={<PracticeRecordingPage />} />
          <Route path="/classes/new" element={<ClassCreate />} />
          <Route path="/classes/:classId" element={<ClassDetail />} />
          {/* Static segment outranks the :lessonId route regardless of order */}
          <Route path="/lesson/new" element={<ManualLessonCompose />} />
          <Route path="/lesson/:lessonId" element={<LessonSummary />} />
          <Route path="/all-lessons" element={<AllLessonsPage />} />
          {/* Takes the recorder could not send, kept in this browser (B6). */}
          <Route path="/recordings/saved" element={<SavedRecordingsPage />} />
          <Route path="/consent-ledger" element={<ConsentLedgerPage />} />
          <Route path="/repertoire" element={<RepertoirePage />} />
          <Route path="/repertoire/saved" element={<SavedRepertoiresPage />} />
          <Route path="/tools" element={<ToolsPage />} />
          <Route path="/metronome" element={<MetronomePage />} />
          <Route path="/tuner" element={<TunerPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Route>
      </Route>
    </Routes>
  );
}

/**
 * Error boundary that catches render errors and shows a fallback UI
 * instead of a white screen.
 */
class ErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("Uncaught error:", error, info);
    analytics.track("error", { message: error?.message, componentStack: info?.componentStack });
    Sentry.captureException(error, { extra: { componentStack: info?.componentStack } });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center text-center p-8 studio-spotlight">
          <div>
            <h1 className="text-3xl font-normal text-[var(--text-primary)] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Something went wrong</h1>
            <p className="text-[var(--text-tertiary)] mb-6 text-sm">An unexpected error occurred.</p>
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="px-6 py-2 bg-[var(--accent-yellow)] text-[var(--text-inverse)] font-medium rounded-full"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Root application component. Wraps everything in BrowserRouter and adds
 * the global toast notification provider.
 * @returns {React.ReactElement}
 */
function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          {/* The theme follows the route: public paths are always dark. */}
          <ThemeRouteSync />
          {/* Visually hidden until focused; lets keyboard users jump past
              the persistent bottom nav to the page's primary content. */}
          <a
            href="#main-content"
            data-testid="skip-to-content"
            className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100]
                       focus:px-4 focus:py-2 focus:rounded-full focus:bg-[var(--accent-yellow)] focus:text-[var(--text-inverse)]
                       focus:font-medium focus:text-sm focus:shadow-lg"
          >
            Skip to main content
          </a>
          <div id="main-content">
            <AppRouter />
          </div>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </ErrorBoundary>
    </div>
  );
}

export default App;
